import React from 'react';
import { AlertTriangle, Clock, Download, Loader2, X } from 'lucide-react';
import {
  buildHistorySyncNotice,
  type HistorySyncAccountLike,
} from '@/lib/whatsapp/historySync';
import { appNavigate } from '@/lib/navigation/appNavigate';

interface HistorySyncBannerProps {
  account?: (HistorySyncAccountLike & { id?: string; name?: string | null }) | null;
  onSync?: () => void;
  syncing?: boolean;
  disabled?: boolean;
  onDismiss?: () => void;
  /** "‹ 1/2 ›" controls when shown inside the top-of-app banner carousel. */
  carouselControls?: React.ReactNode;
  now: Date;
}

const TONE_CLASSES = {
  info: {
    shell: 'border-[#B2DDFF] bg-[#EFF8FF]',
    chip: 'bg-[#D1E9FF] text-[#175CD3]',
    headline: 'text-[#194185]',
    body: 'text-[#475467]',
    action: 'bg-[#175CD3] hover:bg-[#194185] focus-visible:ring-[#2E90FA]/40',
    track: 'bg-[#D1E9FF]',
    fill: 'bg-[#175CD3]',
    dismiss: 'text-[#175CD3] hover:bg-[#D1E9FF]',
  },
  warning: {
    shell: 'border-[#FEDF89] bg-[#FFFAEB]',
    chip: 'bg-[#FEF0C7] text-[#B54708]',
    headline: 'text-[#93370D]',
    body: 'text-[#475467]',
    action: 'bg-[#B54708] hover:bg-[#93370D] focus-visible:ring-[#F79009]/40',
    track: 'bg-[#FEF0C7]',
    fill: 'bg-[#B54708]',
    dismiss: 'text-[#B54708] hover:bg-[#FEF0C7]',
  },
  error: {
    shell: 'border-[#FECDCA] bg-[#FEF3F2]',
    chip: 'bg-[#FEE4E2] text-[#B42318]',
    headline: 'text-[#912018]',
    body: 'text-[#475467]',
    action: 'bg-[#B42318] hover:bg-[#912018] focus-visible:ring-[#F04438]/40',
    track: 'bg-[#FEE4E2]',
    fill: 'bg-[#B42318]',
    dismiss: 'text-[#B42318] hover:bg-[#FEE4E2]',
  },
} as const;

export const HistorySyncBanner: React.FC<HistorySyncBannerProps> = ({
  account,
  onSync,
  syncing = false,
  disabled = false,
  onDismiss,
  carouselControls,
  now,
}) => {
  const notice = buildHistorySyncNotice(account, now);

  if (!notice) return null;

  const tone = TONE_CLASSES[notice.tone];

  const running = notice.progress !== null || notice.syncLabel === 'Syncing…';

  const Icon =
    notice.tone === 'error'
      ? AlertTriangle
      : running
        ? Loader2
        : notice.tone === 'warning'
          ? Clock
          : Download;

  return (
    <div
      className={`z-50 flex w-full items-start justify-between gap-3 border-b px-4 py-2.5 ${tone.shell}`}
      data-testid="history-sync-banner"
      data-tone={notice.tone}
      data-status={notice.status ?? 'none'}
      role="status"
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <span
          className={`mt-px inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${tone.chip}`}
        >
          <Icon
            className={`h-3.5 w-3.5 ${running ? 'animate-spin' : ''}`}
            strokeWidth={2.25}
            aria-hidden
          />
        </span>
        <div className="min-w-0 flex-1">
          <p
            className={`truncate text-[14px] font-semibold leading-5 ${tone.headline}`}
            data-testid="history-sync-banner-headline"
          >
            {notice.headline}
          </p>
          <p
            className={`line-clamp-2 min-h-8 text-[12px] font-medium leading-4 ${tone.body}`}
            data-testid="history-sync-banner-guidance"
          >
            {notice.guidance}
          </p>
          {notice.progress !== null && (
            <div
              className={`mt-1 h-1.5 w-full max-w-[280px] overflow-hidden rounded-full ${tone.track}`}
              role="progressbar"
              aria-valuenow={notice.progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Chat history import progress"
              data-testid="history-sync-banner-progress"
            >
              <div
                className={`h-full rounded-full transition-[width] duration-500 ${tone.fill}`}
                style={{ width: `${notice.progress}%` }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 self-center sm:gap-2">
        {notice.fixAction && window.location.pathname !== notice.fixAction.to && (
          <button
            type="button"
            onClick={() => appNavigate(notice.fixAction!.to)}
            data-testid="history-sync-banner-fix"
            className={`inline-flex h-8 items-center whitespace-nowrap rounded-lg px-3 text-[12px] font-semibold text-white transition focus-visible:outline-none focus-visible:ring-2 ${tone.action}`}
          >
            {notice.fixAction.label}
          </button>
        )}
        {notice.showSync && onSync && (
          <button
            type="button"
            onClick={onSync}
            disabled={syncing || disabled}
            data-testid="history-sync-banner-cta"
            className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[12px] font-semibold text-white transition focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-60 ${tone.action}`}
          >
            {syncing ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.25} />
                Starting…
              </>
            ) : (
              notice.syncLabel
            )}
          </button>
        )}
        {carouselControls}
        <button
          type="button"
          onClick={() => onDismiss?.()}
          className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition ${tone.dismiss}`}
          aria-label="Dismiss banner"
          data-testid="history-sync-banner-dismiss"
        >
          <X className="h-4 w-4" strokeWidth={2.25} />
        </button>
      </div>
    </div>
  );
};
